<template>
    <div class="col-lg-9">
        <div class="d-flex justify-content-between align-items-center mb-4">
            <h4 class="text-7">Business & Banking Information</h4>
            <a href="/business/events" class="btn btn-link text-decoration-none">
                <i class="fas fa-arrow-left"></i> Back to Events
            </a>
        </div>
        <!-- Error Message Display -->
        <div v-if="error" class="alert alert-danger">{{ message }}</div>

        <!-- Business Documents Form -->
        <div class="card card-body border-0 mb-5">
            <h6 class="mb-4 border-bottom pb-2">Business Documents</h6>
            <!-- Success Banner for Business Documents -->
            <div v-if="businessDocumentsUploaded" class="alert alert-success d-flex justify-content-between align-items-center">
                <span>Business documents uploaded successfully!</span>
                <button class="btn btn-link p-0" @click="resetBusinessDocuments">Edit</button>
            </div>
            <!-- Business Documents Form (always visible) -->
            <div class="form-group">
                <label>Select Entity Type</label>
                <select v-model="selectedEntityType" class="form-control">
                    <option disabled value="">-- Select Entity Type --</option>
                    <option value="companies">Companies</option>
                    <option value="registered_charities">Registered Charities</option>
                </select>
            </div>
            <div v-for="(label, index) in documentLabels" :key="index" class="form-group">
                <label>{{ label }}</label>
                <file-upload
                    fileType="pdf"
                    url="/business/biller-onboarding/documents"
                    v-model="documentForm.documents[label]"
                    :documentType="label"
                />
                <div v-if="documentErrors[label]" class="text-danger font-12">{{ documentErrors[label] }}</div>
            </div>
            <button class="btn btn-primary mt-3" @click="submitBusinessDocuments">
                Save Business Documents
            </button>
        </div>

        <!-- Bank Details Form -->
        <div class="card card-body border-0 mb-5">
            <h6 class="mb-4 border-bottom pb-2">Bank Account Details</h6>
            <!-- Success Banner for Bank Details -->
            <div v-if="bankDetailsSaved" class="alert alert-success d-flex justify-content-between align-items-center">
                <span>Bank details saved successfully!</span>
                <button class="btn btn-link p-0" @click="resetBankDetails">Edit</button>
            </div>
            <!-- Bank Details Form (always visible) -->
            <h6 class="mt-4">USD Bank Details</h6>
            <div class="form-group">
                <label>Bank Name</label>
                <input
                    type="text"
                    v-model="bankForm.usd_bank_name"
                    class="form-control"
                    placeholder="Enter bank name"
                    list="usdBanks"
                />
                <datalist id="usdBanks">
                    <option v-for="bank in banks" :key="bank" :value="bank" />
                </datalist>
                <div v-if="bankErrors['usd_bank_name']" class="text-danger font-12">
                    {{ bankErrors['usd_bank_name'] }}
                </div>
            </div>
            <div class="form-group">
                <label>Account Name</label>
                <input
                    type="text"
                    v-model="bankForm.usd_account_name"
                    class="form-control"
                    placeholder="Enter account name"
                />
                <div v-if="bankErrors['usd_account_name']" class="text-danger font-12">
                    {{ bankErrors['usd_account_name'] }}
                </div>
            </div>
            <div class="form-group">
                <label>Account Number</label>
                <input
                    type="text"
                    v-model="bankForm.usd_account_number"
                    class="form-control"
                    placeholder="Enter account number"
                />
                <div v-if="bankErrors['usd_account_number']" class="text-danger font-12">
                    {{ bankErrors['usd_account_number'] }}
                </div>
            </div>
            <div class="form-group">
                <label>Branch</label>
                <input
                    type="text"
                    v-model="bankForm.usd_branch"
                    class="form-control"
                    placeholder="Enter branch name"
                />
                <div v-if="bankErrors['usd_branch']" class="text-danger font-12">
                    {{ bankErrors['usd_branch'] }}
                </div>
            </div>
            <button class="btn btn-primary mt-3" @click="submitBankDetails">
                Save Bank Details
            </button>
        </div>
    </div>
</template>

<script>
import FileUpload from "../../corporate/components/FileUpload.vue";

export default {
    components: { FileUpload },
    data() {
        return {
            selectedEntityType: "",
            documentLabels: [],
            documentForm: { documents: {} },
            documentErrors: {},
            bankForm: {
                usd_bank_name: "",
                usd_account_name: "",
                usd_account_number: "",
                usd_branch: "",
                zwg_bank_name: "",
                zwg_account_name: "",
                zwg_account_number: "",
                zwg_branch: "",
            },
            bankErrors: {},
            banks: [
                "Agribank",
                "BancABC",
                "CABS",
                "CBZ Bank",
                "Ecobank Zimbabwe",
                "FBC Bank",
                "First Capital Bank",
                "IDBZ",
                "Metbank",
                "National Building Society",
                "Nedbank Zimbabwe",
                "NMB Bank",
                "POSB",
                "Stanbic Bank",
                "Standard Chartered",
                "Steward Bank",
                "ZB Bank"
            ],
            error: false,
            message: "",
            businessDocumentsUploaded: false,
            bankDetailsSaved: false,
            companies: [
                "Certificate of Incorporation",
                "CR6 Form",
                "Board Resolution",
                "Bank Account Verification Letter"
            ],
            registered_charities: [
                "Certificate of Registration",
                "List of Trustees",
                "Signed Board Resolution",
                "Bank Verification Letter",
                "Beneficiary Information"
            ],
            unregistered_charities: [
                "Trust Deed",
                "Signed Resolution",
                "Bank Verification Letter",
                "Beneficiary Information"
            ]
        };
    },
    watch: {
        selectedEntityType(newVal) {
            this.documentLabels = this[newVal] || [];
        }
    },
    methods: {
        // Handle input event from FileUpload and wrap File objects in an object if necessary.
        handleDocumentInput(label, value) {
            if (value instanceof File) {
                this.$set(this.documentForm.documents, label, { file: value });
            } else {
                this.$set(this.documentForm.documents, label, value);
            }
        },
        // BUSINESS DOCUMENTS
        submitBusinessDocuments() {
            if (this.documentLabels.every(label => this.documentForm.documents[label])) {
                this.businessDocumentsUploaded = true;
            } else {
                this.error = true;
                this.message = "Please upload all required documents.";
            }
        },
        resetBusinessDocuments() {
            this.businessDocumentsUploaded = false;
        },
        // BANK DETAILS
        async submitBankDetails() {
            this.loading = true;
            this.error = false;
            this.message = "";

            const zwgFieldsComplete =
                this.bankForm.zwg_bank_name &&
                this.bankForm.zwg_account_name &&
                this.bankForm.zwg_account_number &&
                this.bankForm.zwg_branch;

            const usdFieldsComplete =
                this.bankForm.usd_bank_name &&
                this.bankForm.usd_account_name &&
                this.bankForm.usd_account_number &&
                this.bankForm.usd_branch;

            if (!zwgFieldsComplete || !usdFieldsComplete) {
                this.error = true;
                this.message = "Please complete all fields for ZWG and USD account details.";
                this.loading = false;
                return;
            }
            this.bankForm.event_id = this.ticketForm.event_id;
            this.bankForm
                .submit(`/business/event-onboarding/bank-details`)
                .then((response) => {
                    this.stageComplete = true
                    this.message = response.data.message;
                    if(this.stageComplete === true) {
                        setTimeout(function () {
                            this.stageComplete = false;
                            this.stage = 4;
                            this.message = "";
                        }.bind(this), 3000);
                    }
                    this.loading = false;
                })
                .catch((error) => {
                    this.error = true;
                    this.message = "Please complete all fields for ZWG and USD account details.";
                    this.loading = false;
                });
        },

        resetBankDetails() {
            this.bankDetailsSaved = false;
        }
    }
};
</script>


<style scoped>
.alert {
    margin-bottom: 1rem;
}
</style>
